/* eslint-disable react-refresh/only-export-components */
/**
 * @fileoverview Organization settings context and hooks.
 * Provides reporting currency and allowed currencies for the active organization,
 * backed by React Query and normalized API responses.
 * @module features/settings/context/OrgSettingsProvider
 */

import { createContext, useContext, useMemo, type ReactElement, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import {
  OrgSettingsResponseSchema,
  getOrgSettings,
} from '@/features/settings/api/orgSettingsService'
import { prefixWithOrg } from '@/shared/utils/queryKeys'
import { OrgIdSchema } from '@/features/orgs/schemas/orgSchemas'

/**
 * Context value for organization settings.
 */
export interface OrgSettingsContextValue {
  /** Current reporting currency code (ISO-4217), or null when not available */
  reportingCurrency: string | null
  /** Allowed reporting currencies for this org (ISO-4217 codes) */
  allowedReportingCurrencies: string[]
  /** Whether settings are being loaded */
  isLoading: boolean
  /** Optional error message when loading fails */
  error?: string
}

const OrgSettingsContext = createContext<OrgSettingsContextValue | undefined>(undefined)

interface OrgSettingsProviderProps {
  /** Child components that need access to org settings */
  children: ReactNode
}

/**
 * Provider that fetches and exposes organization settings for the active org.
 *
 * - Uses React Query and is automatically scoped by activeOrgId
 * - Normalizes server responses using OrgSettingsResponseSchema
 *
 * @component
 * @example
 * ```tsx
 * <OrgSettingsProvider>
 *   <YourApp />
 * </OrgSettingsProvider>
 * ```
 */
export function OrgSettingsProvider({ children }: OrgSettingsProviderProps): ReactElement {
  const { activeOrgId } = useActiveOrg()

  const query = useQuery({
    queryKey: activeOrgId
      ? prefixWithOrg(activeOrgId as unknown as string, ['settings'])
      : ['settings', 'no-org'],
    queryFn: async () => {
      if (!activeOrgId) throw new Error('No active organization')
      const orgId = OrgIdSchema.parse(activeOrgId)
      const res = await getOrgSettings(orgId)
      return OrgSettingsResponseSchema.parse(res)
    },
    enabled: Boolean(activeOrgId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const value = useMemo<OrgSettingsContextValue>(() => {
    if (query.isError) {
      return {
        reportingCurrency: null,
        allowedReportingCurrencies: [],
        isLoading: false,
        error: (query.error as Error)?.message ?? 'Failed to load organization settings',
      }
    }
    const reportingCurrency = query.data?.reportingCurrency ?? null
    const allowedReportingCurrencies = query.data?.allowedReportingCurrencies ?? []
    return {
      reportingCurrency,
      allowedReportingCurrencies,
      isLoading: query.isLoading,
    }
  }, [query.isError, query.error, query.data, query.isLoading])

  return <OrgSettingsContext.Provider value={value}>{children}</OrgSettingsContext.Provider>
}

/**
 * Hook to access full organization settings from context.
 * @throws Error when used outside of OrgSettingsProvider
 */
export function useOrgSettings(): OrgSettingsContextValue {
  const ctx = useContext(OrgSettingsContext)
  if (!ctx) throw new Error('useOrgSettings must be used within OrgSettingsProvider')
  return ctx
}

/**
 * Convenience hook that returns only the reporting currency and basic state.
 * @returns Currency code (or null), loading flag, and optional error
 * @example
 * ```tsx
 * const { currency, isLoading } = useReportingCurrency();
 * ```
 */
export function useReportingCurrency(): {
  currency: string | null
  isLoading: boolean
  error?: string
} {
  const { reportingCurrency, isLoading, error } = useOrgSettings()
  const base = { currency: reportingCurrency, isLoading } as {
    currency: string | null
    isLoading: boolean
    error?: string
  }
  return error ? { ...base, error } : base
}
